import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Label';
import StopSaleModal from '../../components/modals/StopSaleModal';
import { salesApiService } from '../../api/salesApi';
import {
  CalendarIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const monthNames = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

const CHECKIN_DATE_TYPE_PATTERN = /(check[\s-]?in|c[\s-]?in|arrival)/i;

const parseArrayResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (response?.success && Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.results)) {
    return response.results;
  }

  if (response && typeof response === 'object') {
    return Object.values(response).find((value) => Array.isArray(value)) || [];
  }

  return [];
};

const normalizeText = (value) => String(value || '').trim();

const isAllValue = (value) => {
  const normalizedValue = normalizeText(value).toLowerCase();
  return !normalizedValue || ['all', 'tumu', 'tümü', 'hepsi'].includes(normalizedValue);
};

const getOpenStopTypeName = (stopSale) =>
  normalizeText(stopSale?.openstoptype?.type || stopSale?.openstoptype_name || stopSale?.type);

const isOpenSaleRecord = (stopSale) => getOpenStopTypeName(stopSale).toLowerCase() === 'open sales';

const isStopSaleRecord = (stopSale) => getOpenStopTypeName(stopSale).toLowerCase() === 'stop sales';

const isCheckinDateType = (stopSale) => CHECKIN_DATE_TYPE_PATTERN.test(normalizeText(stopSale?.date_type_name));

const isAgencySpecificRecord = (stopSale) => !isAllValue(stopSale?.agency_name);

const isMatchingCellRecord = ({ stopSale, dateString, selectedHotelName, roomTypeName }) => {
  const startDate = normalizeText(stopSale?.start_date);
  const finishDate = normalizeText(stopSale?.finish_date);

  if (!startDate || !finishDate || startDate === 'null' || finishDate === 'null') {
    return false;
  }

  const isInDateRange = dateString >= startDate && dateString <= finishDate;
  const hotelMatch = selectedHotelName && stopSale?.hotel_name === selectedHotelName;
  const roomTypeMatch =
    stopSale?.roomtype_name === roomTypeName ||
    stopSale?.roomtype_name === 'ALL' ||
    stopSale?.roomtype_name === 'All';

  return isInDateRange && hotelMatch && roomTypeMatch && (isOpenSaleRecord(stopSale) || isStopSaleRecord(stopSale));
};

const getPeriodDates = (startDate, days) =>
  Array.from({ length: days }, (_, index) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + index);
    return date;
  });

const StopSaleCalendar = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stopSales, setStopSales] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentStartDate, setCurrentStartDate] = useState(new Date());
  const goToPreviousMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
    setCurrentStartDate(new Date());
  };

  const goToPreviousPeriod = () => {
    if (viewMode === '7days') {
      setCurrentStartDate((prev) => new Date(prev.getTime() - 7 * 24 * 60 * 60 * 1000));
    } else if (viewMode === '14days') {
      setCurrentStartDate((prev) => new Date(prev.getTime() - 14 * 24 * 60 * 60 * 1000));
    } else if (viewMode === '21days') {
      setCurrentStartDate((prev) => new Date(prev.getTime() - 21 * 24 * 60 * 60 * 1000));
    }
  };

  const goToNextPeriod = () => {
    if (viewMode === '7days') {
      setCurrentStartDate((prev) => new Date(prev.getTime() + 7 * 24 * 60 * 60 * 1000));
    } else if (viewMode === '14days') {
      setCurrentStartDate((prev) => new Date(prev.getTime() + 14 * 24 * 60 * 60 * 1000));
    } else if (viewMode === '21days') {
      setCurrentStartDate((prev) => new Date(prev.getTime() + 21 * 24 * 60 * 60 * 1000));
    }
  };

  const fetchStopSales = async () => {
    setLoading(true);
    try {
      const response = await salesApiService.getOpenStopSales();
      setStopSales(parseArrayResponse(response));
    } catch {
      setStopSales([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHotels = async () => {
    try {
      const response = await salesApiService.getHotels();
      const hotelsData = parseArrayResponse(response);
      setHotels(hotelsData);

      if (hotelsData.length > 0) {
        setSelectedHotel(String(hotelsData[0].id));
      }
    } catch {
      setHotels([]);
    }
  };

  const loadRoomTypes = async (hotelId = null) => {
    try {
      const response = await salesApiService.getRoomTypes();
      const allRoomTypes = parseArrayResponse(response);
      let filteredRoomTypes = allRoomTypes;

      if (hotelId) {
        const normalizedHotelId = Number(hotelId);
        filteredRoomTypes = allRoomTypes.filter(
          (roomType) =>
            roomType.hotel_id === normalizedHotelId ||
            roomType.hotel === normalizedHotelId ||
            roomType.hotelId === normalizedHotelId
        );
      }

      setRoomTypes(filteredRoomTypes);
    } catch {
      setRoomTypes([]);
    }
  };

  const handleSaveStopSale = async (data) => {
    try {
      setLoading(true);

      if (Array.isArray(data)) {
        await salesApiService.createOpenStopSalesBulk(data);
        setIsModalOpen(false);
        await fetchStopSales();
        return;
      }

      if (data.id) {
        await salesApiService.updateOpenStopSale(data.id, data);
      } else {
        await salesApiService.createOpenStopSale(data);
      }

      setIsModalOpen(false);
      await fetchStopSales();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStopSales();
    loadHotels();
    loadRoomTypes();
  }, []);

  useEffect(() => {
    if (selectedHotel) {
      loadRoomTypes(selectedHotel);
    }
  }, [selectedHotel]);

  const handleHotelChange = (e) => {
    setSelectedHotel(e.target.value);
  };

  const handleCreateStopSale = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    fetchStopSales();
  };

  const selectedHotelName = useMemo(
    () => hotels.find((hotel) => hotel.id === Number(selectedHotel))?.hotel_name || '',
    [hotels, selectedHotel]
  );

  const monthDates = useMemo(
    () =>
      Array.from(
        { length: new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate() },
        (_, index) => new Date(currentMonth.getFullYear(), currentMonth.getMonth(), index + 1)
      ),
    [currentMonth]
  );

  const getCellData = (dateString, roomTypeName) => {
    const matchingRecords = stopSales.filter((stopSale) =>
      isMatchingCellRecord({
        stopSale,
        dateString,
        selectedHotelName,
        roomTypeName
      })
    );

    const stopRecords = matchingRecords.filter(isStopSaleRecord);
    const openRecords = matchingRecords.filter(isOpenSaleRecord);
    const agencyStopRecords = stopRecords.filter(isAgencySpecificRecord);
    const tooltipAgencies = [...new Set(agencyStopRecords.map((record) => normalizeText(record.agency_name)).filter(Boolean))];
    const tooltipNotes = [...new Set(stopRecords.map((record) => normalizeText(record.note || record.notes)).filter(Boolean))];

    if (agencyStopRecords.some(isCheckinDateType)) {
      return {
        tone: 'stop',
        lines: ['AGENCY', 'CHECKIN STOP'],
        tooltipAgencies,
        tooltipNotes
      };
    }

    if (agencyStopRecords.length > 0) {
      return {
        tone: 'stop',
        lines: ['AGENCY', 'STOP'],
        tooltipAgencies,
        tooltipNotes
      };
    }

    if (stopRecords.some(isCheckinDateType)) {
      return {
        tone: 'stop',
        lines: ['C-IN', 'STOP'],
        tooltipAgencies: [],
        tooltipNotes
      };
    }

    if (stopRecords.length > 0) {
      return {
        tone: 'stop',
        lines: ['STOP', 'SALE'],
        tooltipAgencies: [],
        tooltipNotes
      };
    }

    if (openRecords.length > 0) {
      return {
        tone: 'open',
        lines: ['OPEN', 'SALE'],
        tooltipAgencies: [],
        tooltipNotes: []
      };
    }

    return null;
  };

  const renderCell = (date, roomTypeName, key) => {
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const isToday = date.toDateString() === new Date().toDateString();
    const cellData = getCellData(dateString, roomTypeName);
    const toneClass =
      cellData?.tone === 'open'
        ? 'text-green-600 dark:text-green-400'
        : 'text-red-600 dark:text-red-400';

    return (
      <td
        key={key}
        className={`group relative border border-gray-300 dark:border-gray-600 px-1 py-2 text-center text-xs w-12 ${
          isToday ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-800'
        }`}
      >
        {cellData ? (
          <>
            <div className="flex flex-col items-center justify-center">
              {cellData.lines.map((line, index) => (
                <span
                  key={`${line}-${index}`}
                  className={`${toneClass} font-bold leading-tight ${index === 0 ? 'text-[10px]' : 'text-[9px]'}`}
                >
                  {line}
                </span>
              ))}
            </div>

            {(cellData.tooltipAgencies.length > 0 || cellData.tooltipNotes.length > 0) && (
              <div className="pointer-events-none invisible absolute bottom-full left-1/2 z-30 mb-2 w-max min-w-[140px] -translate-x-1/2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-left text-[10px] text-gray-700 shadow-lg group-hover:visible dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
                {cellData.tooltipAgencies.length > 0 && (
                  <>
                    <div className="mb-1 font-semibold text-gray-900 dark:text-white">Acenteler</div>
                    {cellData.tooltipAgencies.map((agency) => (
                      <div key={agency} className="leading-tight">
                        {agency}
                      </div>
                    ))}
                  </>
                )}

                {cellData.tooltipNotes.length > 0 && (
                  <>
                    <div className={`${cellData.tooltipAgencies.length > 0 ? 'mt-2' : 'mb-1'} font-semibold text-gray-900 dark:text-white`}>
                      Açıklama
                    </div>
                    {cellData.tooltipNotes.map((note, index) => (
                      <div key={`${note}-${index}`} className="max-w-[220px] whitespace-normal leading-tight">
                        {note}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
        )}
      </td>
    );
  };

  const renderTable = (dates) => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 text-xs table-fixed">
        <thead>
          <tr>
            <th className="border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 px-2 py-2 text-left text-xs font-medium text-gray-900 dark:text-white w-32">
              ODA TİPİ
            </th>
            {dates.map((date, index) => {
              const isToday = date.toDateString() === new Date().toDateString();
              return (
                <th
                  key={`${date.toISOString()}-${index}`}
                  className={`border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 px-1 py-2 text-center text-xs font-medium w-12 ${
                    isToday
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                      : 'text-gray-900 dark:text-white'
                  }`}
                >
                  {date.getDate()}.{date.getMonth() + 1}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {roomTypes.length > 0 ? (
            roomTypes.map((roomType) => (
              <tr key={roomType.id}>
                <td className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-2 py-2 text-xs font-medium text-gray-900 dark:text-white w-32">
                  {roomType.roomtype_name}
                </td>
                {dates.map((date, index) => renderCell(date, roomType.roomtype_name, `${roomType.id}-${index}`))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={dates.length + 1} className="text-center py-4 text-gray-500 dark:text-gray-400">
                Room types yukleniyor...
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-full mx-auto px-3 sm:px-4 md:pl-6 md:pr-2 py-3 md:py-4">
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                Stop Sale Takvim
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Oda satış durdurma işlemlerini takvim görünümünde yönetin
              </p>
            </div>
            
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-start">
            <Button
              onClick={handleCreateStopSale}
              variant="ghost"
              size="icon"
              className="text-gray-500 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400"
              title="Yeni Kayıt"
            >
              <PlusIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <Card className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-3">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Hotel Seçimi:</Label>
                  <select
                    value={selectedHotel}
                    onChange={handleHotelChange}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {hotels.map((hotel) => (
                      <option key={hotel.id} value={hotel.id}>
                        {hotel.hotel_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {roomTypes.length} oda tipi yüklendi
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'calendar'
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <CalendarIcon className="w-3 h-3 mr-1" />
              Takvim
            </button>
          </div>
          
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setViewMode('7days')}
              className={`flex items-center px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                viewMode === '7days'
                  ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              7 Günlük
            </button>
            <button
              onClick={() => setViewMode('14days')}
              className={`flex items-center px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                viewMode === '14days'
                  ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              14 Günlük
            </button>
            <button
              onClick={() => setViewMode('21days')}
              className={`flex items-center px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                viewMode === '21days'
                  ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              21 Günlük
            </button>
          </div>
        </div>

        {viewMode === 'calendar' && (
          <Card>
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <div></div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={goToPreviousMonth}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <ChevronLeftIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  
                  <h2 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </h2>
                  
                  <button
                    onClick={goToNextMonth}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <ChevronRightIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  
                  <button
                    onClick={goToToday}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs"
                  >
                    Bugün
                  </button>
                </div>
              </div>
              {renderTable(monthDates)}
            </div>
          </Card>
        )}

        {viewMode === '7days' && (
          <Card>
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">7 Günlük Stop Sale Tablosu</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={goToPreviousPeriod}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <ChevronLeftIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {currentStartDate.toLocaleDateString('tr-TR')} - {new Date(currentStartDate.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('tr-TR')}
                  </span>
                  
                  <button
                    onClick={goToNextPeriod}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <ChevronRightIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  
                  <button
                    onClick={goToToday}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs"
                  >
                    Bugün
                  </button>
                </div>
              </div>
              {renderTable(getPeriodDates(currentStartDate, 7))}
            </div>
          </Card>
        )}

        {viewMode === '14days' && (
          <Card>
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">14 Günlük Stop Sale Tablosu</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={goToPreviousPeriod}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <ChevronLeftIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {currentStartDate.toLocaleDateString('tr-TR')} - {new Date(currentStartDate.getTime() + 13 * 24 * 60 * 60 * 1000).toLocaleDateString('tr-TR')}
                  </span>
                  
                  <button
                    onClick={goToNextPeriod}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <ChevronRightIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  
                  <button
                    onClick={goToToday}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs"
                  >
                    Bugün
                  </button>
                </div>
              </div>
              {renderTable(getPeriodDates(currentStartDate, 14))}
            </div>
          </Card>
        )}

        {viewMode === '21days' && (
          <Card>
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">21 Günlük Stop Sale Tablosu</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={goToPreviousPeriod}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <ChevronLeftIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {currentStartDate.toLocaleDateString('tr-TR')} - {new Date(currentStartDate.getTime() + 20 * 24 * 60 * 60 * 1000).toLocaleDateString('tr-TR')}
                  </span>
                  
                  <button
                    onClick={goToNextPeriod}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <ChevronRightIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  
                  <button
                    onClick={goToToday}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs"
                  >
                    Bugün
                  </button>
                </div>
              </div>
              {renderTable(getPeriodDates(currentStartDate, 21))}
            </div>
          </Card>
        )}

        {loading && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Yükleniyor...</span>
          </div>
        )}
      </div>

      {isModalOpen && (
        <StopSaleModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleSaveStopSale}
        />
      )}
    </div>
  );
};

export default StopSaleCalendar;
